import React from "react";
import { Marker, Popup } from "react-leaflet";
import * as L from "leaflet";
import "../shared/Heatmap.css";
import type { MerchantModel } from "../../../types/Merchant";
import {
  DEFAULT_ORGANIZATION_MAP_CONFIG,
  type OrganizationMapConfig,
} from "../../../commons/Constant";
import MapGeoJsonBoundaryLayer from "../../../../../components/customs/map/MapGeoJsonBoundaryLayer";
import MapPopupController from "../../../../../components/customs/map/MapPopupController";
import MapSurface from "../../../../../components/customs/map/MapSurface";
import MapViewportController from "../../../../../components/customs/map/MapViewportController";

// Category color mapping based on Figma design
const getCategoryColor = (categoryName?: string): string => {
  const categoryColorMap: Record<string, string> = {
    "Food & Beverage": "#ff6900",
    "Health & Wellness": "#00c950",
    Retail: "#ad46ff",
    "Beauty & Personal care": "#f6339a",
    Education: "#3b82f6",
    Grocery: "#14b8a6",
    Auto: "#ef4444",
    Transport: "#f59e0b",
    Travel: "#06b6d4",
    Entertainment: "#3339f6",
  };
  return categoryColorMap[categoryName || ""] || "#6a7282"; // Default gray
};

// House icon SVG (provided by user)
const HOUSE_ICON_SVG = `
<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10 14V10.6667C10 10.4899 9.92976 10.3203 9.80474 10.1953C9.67971 10.0702 9.51014 10 9.33333 10H6.66667C6.48986 10 6.32029 10.0702 6.19526 10.1953C6.07024 10.3203 6 10.4899 6 10.6667V14" stroke="white" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11.8491 6.87325C11.7102 6.74021 11.5252 6.66595 11.3328 6.66595C11.1404 6.66595 10.9554 6.74021 10.8165 6.87325C10.5065 7.16893 10.0945 7.33389 9.66614 7.33389C9.23774 7.33389 8.8258 7.16893 8.51581 6.87325C8.37686 6.74041 8.19204 6.66627 7.99981 6.66627C7.80757 6.66627 7.62275 6.74041 7.4838 6.87325C7.17378 7.16913 6.76169 7.33421 6.33314 7.33421C5.90458 7.33421 5.4925 7.16913 5.18247 6.87325C5.0435 6.74021 4.85853 6.66595 4.66614 6.66595C4.47375 6.66595 4.28878 6.74021 4.14981 6.87325C3.85037 7.15899 3.45532 7.3231 3.04156 7.33362C2.62779 7.34414 2.22492 7.20033 1.91134 6.93017C1.59777 6.66002 1.39594 6.28285 1.34513 5.87208C1.29432 5.46131 1.39817 5.04633 1.63647 4.70792L3.56247 1.91859C3.68468 1.73826 3.8492 1.59062 4.04166 1.48858C4.23411 1.38654 4.44864 1.33321 4.66647 1.33325H11.3331C11.5503 1.33317 11.7643 1.38615 11.9563 1.48758C12.1484 1.58901 12.3127 1.73582 12.4351 1.91525L14.3651 4.70992C14.6035 5.0486 14.7072 5.4639 14.6561 5.87488C14.605 6.28586 14.4027 6.66308 14.0886 6.93305C13.7745 7.20301 13.3712 7.34637 12.9572 7.33519C12.5432 7.32401 12.1482 7.1591 11.8491 6.87259" stroke="white" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M2.6665 7.30005V12.6667C2.6665 13.0203 2.80698 13.3595 3.05703 13.6095C3.30708 13.8596 3.64622 14 3.99984 14H11.9998C12.3535 14 12.6926 13.8596 12.9426 13.6095C13.1927 13.3595 13.3332 13.0203 13.3332 12.6667V7.30005" stroke="white" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;

// Create merchant icon with category color background and house icon in center (circular shape matching Figma)
const merchantIcon = (categoryName?: string) => {
  const color = getCategoryColor(categoryName);

  // Circular icon with colored background, white border, and house icon in center (matching Figma design)
  const merchantIconSVG = `
        <svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="17.5" cy="17.5" r="15.5" fill="${color}" stroke="white" stroke-width="2"/>
            <g transform="translate(9.5, 9.5)">
                ${HOUSE_ICON_SVG}
            </g>
        </svg>
    `;

  return L.divIcon({
    html: merchantIconSVG,
    className: "",
    iconSize: [35, 35],
    iconAnchor: [17.5, 17.5],
    popupAnchor: [0, -17.5],
  });
};

type MerchantMapProps = {
  merchants: MerchantModel[] | null;
  selectedMerchant: MerchantModel | null;
  merchantName?: string;
  filterRevision?: number;
  mapConfig?: OrganizationMapConfig;
  onSelectMerchant?: (merchant: MerchantModel | null) => void;
};

const MerchantMap: React.FC<MerchantMapProps> = ({
  merchants,
  selectedMerchant,
  merchantName,
  filterRevision,
  mapConfig = DEFAULT_ORGANIZATION_MAP_CONFIG,
  onSelectMerchant,
}) => {
  // Filter merchants by search query
  const filteredMerchants = React.useMemo(() => {
    if (!merchants) return [];
    if (!merchantName || merchantName.trim() === "") return merchants;

    const searchTerm = merchantName.toLowerCase().trim();
    return merchants.filter(
      (merchant) =>
        merchant.merchant_name.toLowerCase().includes(searchTerm) ||
        merchant.address.toLowerCase().includes(searchTerm) ||
        merchant.category?.name.toLowerCase().includes(searchTerm),
    );
  }, [merchants, merchantName]);

  return (
    <div className="h-full w-full p-4">
      <MapSurface
        initialViewport={{ center: mapConfig.center, zoom: mapConfig.zoom }}
        style={{ height: "100%", width: "100%", borderRadius: "1rem" }}
        maxBounds={mapConfig.maxBounds}
        maxBoundsViscosity={1.0}
        minZoom={10}
        maxZoom={18}
        className="rounded-2xl overflow-hidden"
      >
        <MapViewportController
          viewport={{ center: mapConfig.center, zoom: mapConfig.zoom }}
          revision={filterRevision}
          transition="set"
        />
        <MapPopupController selection={selectedMerchant} />
        <MapGeoJsonBoundaryLayer data={mapConfig.boundary} />

        {/* Merchant markers with category colors */}
        {filteredMerchants?.map((merchant, idx) => (
          <Marker
            position={[merchant?.latitude, merchant?.longitude]}
            key={merchant?.merchant_id || idx}
            icon={merchantIcon(merchant?.category?.name)}
            eventHandlers={{
              click: () => onSelectMerchant?.(merchant as MerchantModel),
              popupclose: () => onSelectMerchant?.(null),
            }}
          >
            <Popup>
              <div className="text-[#00BCD4] font-semibold">{merchant?.merchant_name}</div>
              <div className="text-[#4A5565] text-sm">{merchant?.address}</div>
              <div className="text-[#4A5565] text-xs mt-1">{merchant?.category?.name}</div>
            </Popup>
          </Marker>
        ))}
      </MapSurface>
    </div>
  );
};

export default MerchantMap;
